import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { ButtonColor, ButtonSize, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { List, Map } from "immutable/dist/immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
  AddonsMethodsResponseDto,
  ConsolidationAddonsMethodsResponseDto,
  PackingMethodsResponseDto,
} from "types/api/cart";
import { ShipmentsUpdateCommentDto } from "types/api/shipments";
import { OpenRightPanel } from "types/common/rightPanel";
import Button from "../../../../components/Button/Button";
import ImageViewer2 from "../../../../components/ImageViewer2/ImageViewer2";
import PackageSizes from "../../../../components/PackageSizes/PackageSizes";
import PackagesList from "../../../../components/PackagesList/PackagesList";
import PackagesPagination from "../../../../components/PackagesPagination/PackagesPagination";
import PackingOptionsList from "../../../../components/PackingOptionsList/PackingOptionsList";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import {
  createCustomsDeclaration,
  getConsolidationShipmentsCsv,
  getDetailedShipment,
  updateComment,
} from "../../../../reduxFolder/reducers/outgoing";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  getAddonsMethods,
  getConsolidationAddonsMethods,
  getPackingMethods,
} from "../../../../reduxFolder/reducers/services";
import { authSelectUnits } from "../../../../reduxFolder/selectors/globalSelectors";
import {
  outgoingSelectDetailedItems,
  outgoingSelectDetailedLoading,
} from "../../../../reduxFolder/selectors/outgoingSelectors";
import { panelSelectData } from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  servicesSelectAddons,
  servicesSelectConsolidationAddons,
  servicesSelectPackingMethods,
} from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  ExcessMarker,
  HeaderTitle,
  InnerSection,
  Section,
  SectionContent,
  SectionHeader,
} from "../../../../styles/outgoingStyles";
import { ChangeButton } from "../../../../styles/parcelsStyles";
import formatter from "../../../../utils/formatter";
import { useDemoHint } from "../../../Storage/components/DemoShipments/useDemoHint";
import csvFileIcon from "../../images/csv-file-icon.png";
import {
  CancelIcon,
  ContentWrapper,
  CsvButton,
  Footer,
  FooterActions,
  ImageSection,
  NextChevron,
  PanelAddressItem,
  PanelLayout,
  SKUTitle,
  Total,
  TotalAmount,
  TotalLabel,
} from "./ParcelInfoTab.styles";

interface ParcelInfoTabProps {
  id?: string | number;
  panelData: any;
  packingMethods: any;
  detailedItem: any;
  addons: any;
  consolidationAddons: any;
  units: string;
  openRightPanel: OpenRightPanel;
  getAddonsMethods: () => Promise<AddonsMethodsResponseDto[]>;
  getPackingMethods: () => Promise<PackingMethodsResponseDto[]>;
  getConsolidationAddonsMethods: () => Promise<
    ConsolidationAddonsMethodsResponseDto[]
  >;
  onPackingCancel?: () => void;
  getDetailedShipment: (id: number) => Promise<void>;
  updateComment: (id: number, data: ShipmentsUpdateCommentDto) => Promise<void>;
  getConsolidationShipmentsCsv: (id: number) => Promise<any>;
}

const ParcelInfoTab: FC<ParcelInfoTabProps> = React.memo(
  ({
    id,
    packingMethods,
    onPackingCancel,
    detailedItem,
    addons,
    getPackingMethods,
    getAddonsMethods,
    consolidationAddons,
    getConsolidationAddonsMethods,
    panelData,
    openRightPanel,
    updateComment,
    getDetailedShipment,
    units,
    getConsolidationShipmentsCsv,
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const showDemoHint = useDemoHint();
    const contentRef = useRef<HTMLDivElement>(null);
    const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
    const isDemo = !!panelData.get("isDemo");
    const detailed = detailedItem.get(id, Map());
    const delivery = detailed.get("preferred_carrier");
    const state = detailed.get("state");
    const packingCode = detailed.getIn(["optional_line_item_codes", 0]);
    const isPaid = state === "paid";
    const disabledChange =
      ["paid", "claimed_for_packing", "picked", "claimed"].indexOf(state) !==
      -1;
    const disabledChaneForPackingGrade = disabledChange || state === "packed";
    const disabledChangeForShippingMethod =
      disabledChange || state !== "pending";
    const hideFooterAction =
      ["claimed_for_packing", "picked", "claimed"].indexOf(state) !== -1;
    const showPackedParcelInformation =
      ["paid", "packed"].indexOf(state) !== -1;
    const showAllPackagesList = !showPackedParcelInformation;
    const showPayButton = state !== "pending" && !isPaid;

    const packing = Map(
      packingMethods.find(
        (packingMethod: any) => packingMethod.get("code") === packingCode,
      ),
    );
    const services = !!packingMethods.size;
    const foundPackageId = panelData.get("foundPackageId", 0);
    const excessItemsOriginal = panelData.get("excessItems");
    const excessItems =
      !!excessItemsOriginal &&
      excessItemsOriginal.map((item: any) => item.get("item"));
    const isHideFooter = hideFooterAction;

    const packages = detailed.get("packages", List());
    const hasFewPackages = packages.count() > 1;
    const items = detailed.get("items");

    useFetchOnceEffect(!packingMethods.size, getPackingMethods);
    useFetchOnceEffect(!addons.size, getAddonsMethods);
    useFetchOnceEffect(
      !consolidationAddons.size,
      getConsolidationAddonsMethods,
    );

    useEffect(() => {
      if (contentRef.current && foundPackageId)
        contentRef.current.scrollIntoView();
    }, [contentRef.current, foundPackageId]);

    const clickChangingAddress = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }
      openRightPanel(
        "CHANGING_ADDRESS",
        panelData.set("addressId", detailed.get("destination_address_id")),
      );
    }, [openRightPanel, panelData, detailed, isDemo, showDemoHint]);

    const editDeliveryMethod = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }
      openRightPanel(
        "DELIVERY_METHODS",
        panelData
          .set("deliveryCountry", detailed.get("country"))
          .set("deliveryMethod", delivery),
      );
    }, [openRightPanel, panelData, detailed, delivery, isDemo, showDemoHint]);

    const editPackingGrade = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }
      openRightPanel(
        "PACKING_GRADE",
        panelData
          // .set('id', id) FIXME:
          .set("consolidationSku", detailed.get("sku"))
          .set("deliveryMethod", delivery)
          .set("packingCode", packingCode)
          .set("packingMethods", packingMethods)
          .set(
            "selectedConsolidationAddons",
            detailed.get("consolidation_addons"),
          ),
      );
    }, [
      openRightPanel,
      panelData,
      detailed,
      delivery,
      packingCode,
      packingMethods,
      isDemo,
      showDemoHint,
    ]);

    const clickPackagesItem = (item: any) => {
      openRightPanel(
        "STORAGE_ITEM",
        Map({
          readOnly: true,
          isDemo,
          id: item.get("shipment_id"),
          originalItem: item,
          detailedItemID: id,
          consolidationSku: detailed.get("sku"),
          emblem_thumb_url: item.get("emblem_thumb_url"),
          description: item.get("shipment_tracking_number"),
          returnPreviousPanel: panelData.get("returnPreviousPanel"),
          excessItems: panelData.get("excessItems"),
          rootReturnPanel: panelData.get("returnPreviousPanel"),
          getShipment: getDetailedShipment,
          updateComment,
          isItemInCart: true,
        }),
      );
    };

    const handleSetSelectPackageIndex = useCallback(
      (index: number) => {
        setSelectedPackageIndex(index);
      },
      [setSelectedPackageIndex],
    );

    const clickReviewAndPay = useCallback(() => {
      if (isDemo) {
        showDemoHint();
        return;
      }
      openRightPanel("CONFIRM_PAYMENT_DETAILS", panelData);
    }, [openRightPanel, panelData, isDemo, showDemoHint]);

    const clickAlreadyPayed = useCallback(() => {
      openRightPanel("REVIEW_AND_PAY", panelData);
    }, [openRightPanel, panelData]);

    const handleCsvClick = () => {
      // The CSV body is server-generated — nothing to export for demo rows.
      if (isDemo) {
        showDemoHint();
        return;
      }
      getConsolidationShipmentsCsv(detailed.get("id")).then((response) => {
        if (response && response.value && response.value.get("data")) {
          const blob = new Blob([response.value.get("data")], {
            type: "text/csv;charset=utf-8;",
          });

          const link = document.createElement("a");
          link.setAttribute("href", URL.createObjectURL(blob));
          link.setAttribute("download", "consolidation-shipments.csv");
          document.body.appendChild(link);

          link.click();
        }
      });
    };

    const rendererFooter = () => {
      if (isHideFooter) return null;

      const payButtonTitle = onPackingCancel
        ? t("common.reviewAndPrepay")
        : t("common.reviewAndPay");
      const total = detailed.getIn(["estimate", "total"]);
      const showTotal = (showPayButton || isPaid) && total != null;

      return (
        <Footer>
          {showTotal && (
            <Total>
              <TotalLabel>
                {isPaid ? t("parcels.paymentPaid") : t("parcels.totalLabel")}
              </TotalLabel>
              <TotalAmount>
                {`${currencySymbol}${formatter.currency(total)}`}
              </TotalAmount>
            </Total>
          )}
          <FooterActions>
            {onPackingCancel && (
              <Button
                onClick={onPackingCancel}
                color={ButtonColor.Danger}
                leading={<CancelIcon type={IconType.Close} />}
              >
                {t("common.cancel")}
              </Button>
            )}
            {showPayButton && (
              <Button
                onClick={clickReviewAndPay}
                color={ButtonColor.Primary}
                variant={ButtonVariant.Filled}
                size={ButtonSize.Large}
                trailing={<NextChevron type={IconType.RightArrow} />}
              >
                {payButtonTitle}
              </Button>
            )}
            {isPaid && (
              <Button
                onClick={clickAlreadyPayed}
                color={ButtonColor.Neutral}
                variant={ButtonVariant.Bordered}
                size={ButtonSize.Large}
              >
                {t("parcels.moreDetails")}
              </Button>
            )}
          </FooterActions>
        </Footer>
      );
    };

    const renderItemsList = (items: any, isExcessItems = false) => {
      const itemsCount = (!!items && items.count()) || 0;
      const localizedTitle = isExcessItems
        ? t("parcels.excessPackagesIncluded")
        : t("parcels.packagesIncluded");
      return itemsCount === 0 ? null : (
        <Section ref={contentRef}>
          <SectionHeader>
            <HeaderTitle>
              {`${localizedTitle} (${itemsCount})`}
              {isExcessItems ? <ExcessMarker /> : null}
              {!isExcessItems && (
                <Tooltip body={t("common.csvShipments")} place="bottom">
                  <CsvButton
                    aria-label={t("common.csvShipments")}
                    onClick={handleCsvClick}
                  >
                    <img
                      src={csvFileIcon}
                      alt="CSV"
                      style={{ height: "24px" }}
                    />
                  </CsvButton>
                </Tooltip>
              )}
            </HeaderTitle>
          </SectionHeader>
          <InnerSection>
            <SectionContent>
              <PackagesList
                extended
                isExcess={isExcessItems}
                items={items}
                foundItemId={foundPackageId}
                handleClick={clickPackagesItem}
              />
            </SectionContent>
          </InnerSection>
        </Section>
      );
    };

    const renderPackageInfo = (pkg: any, showMPSInfo = false) => {
      const pictures = pkg.get("pictures", List());
      const items = pkg.get("items");
      const sku = pkg.get("sku");

      const weight = pkg.get("weight");
      const height = pkg.get("height");
      const width = pkg.get("width");
      const depth = pkg.get("depth");

      const itemsUnitsTitle =
        items.count() === 1 ? t("parcels.item") : t("parcels.items");
      const itemsTitle = `${items.count()} ${itemsUnitsTitle}`;
      const weightTitle = `${formatter.weight(weight)} ${t(
        `units.${units}.primaryShort`,
      )}`;
      const itemsPerWeightTitle = `${itemsTitle} / ${weightTitle}`;

      const infoSection = (
        <>
          <SectionHeader>
            <SKUTitle>{sku}</SKUTitle>
          </SectionHeader>
          <SectionContent>{itemsPerWeightTitle}</SectionContent>
          <SectionContent>
            <PackageSizes
              width={width}
              height={height}
              depth={depth}
              units={units}
            />
          </SectionContent>
        </>
      );

      return (
        <>
          {(showMPSInfo || !!pictures.size) && (
            <InnerSection>
              {showMPSInfo ? infoSection : null}
              <SectionContent>
                {!!pictures.size && (
                  <ImageSection>
                    <ImageViewer2 images={pictures.toJS()} />
                  </ImageSection>
                )}
              </SectionContent>
            </InnerSection>
          )}
          {renderItemsList(excessItems, true)}
          {renderItemsList(items)}
        </>
      );
    };

    return (
      services && (
        <PanelLayout>
          <ContentWrapper>
            <Section>
              <SectionHeader>
                <HeaderTitle>{t("parcels.shippingAddress")}</HeaderTitle>
                <ChangeButton
                  hidden={disabledChange}
                  onClick={clickChangingAddress}
                >
                  {t("common.change")}
                </ChangeButton>
              </SectionHeader>
              <PanelAddressItem address={detailed} isHideButtons />
            </Section>
            <Section>
              <SectionHeader>
                <HeaderTitle>{t("parcels.shippingMethod")}</HeaderTitle>
                <ChangeButton
                  hidden={disabledChangeForShippingMethod}
                  onClick={editDeliveryMethod}
                >
                  {t("common.change")}
                </ChangeButton>
              </SectionHeader>
              <InnerSection>
                <SectionContent>
                  <ServiceLogo serviceKey={delivery} />
                  {delivery}
                </SectionContent>
              </InnerSection>
            </Section>
            <Section>
              <SectionHeader>
                <HeaderTitle>{t("parcels.packingGrade")}</HeaderTitle>
                <ChangeButton
                  hidden={disabledChaneForPackingGrade}
                  onClick={editPackingGrade}
                >
                  {t("common.change")}
                </ChangeButton>
              </SectionHeader>
              <InnerSection>
                <PackingOptionsList
                  packing={packing}
                  addonCodes={detailed.get("consolidation_addons")}
                  consolidationAddons={consolidationAddons}
                />
              </InnerSection>
            </Section>
            {showPackedParcelInformation ? (
              <Section>
                <HeaderTitle>
                  {hasFewPackages
                    ? t("parcels.parcelsInformation")
                    : t("parcels.parcelInformation")}
                </HeaderTitle>
                {hasFewPackages && (
                  <InnerSection>
                    <PackagesPagination
                      count={packages.count()}
                      selectedIndex={selectedPackageIndex}
                      onSelect={handleSetSelectPackageIndex}
                    />
                  </InnerSection>
                )}
              </Section>
            ) : null}
            {showPackedParcelInformation &&
              renderPackageInfo(
                packages.get(selectedPackageIndex),
                hasFewPackages,
              )}
            {showAllPackagesList && renderItemsList(excessItems, true)}
            {showAllPackagesList && renderItemsList(items)}
          </ContentWrapper>
          {rendererFooter()}
        </PanelLayout>
      )
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    detailedItem: outgoingSelectDetailedItems,
    packingMethods: servicesSelectPackingMethods,
    isLoadingDetailedItem: outgoingSelectDetailedLoading,
    addons: servicesSelectAddons,
    panelData: panelSelectData,
    units: authSelectUnits,
    consolidationAddons: servicesSelectConsolidationAddons,
  }),
  {
    getPackingMethods,
    createCustomsDeclaration,
    getAddonsMethods,
    getConsolidationAddonsMethods,
    openRightPanel,
    getDetailedShipment,
    updateComment,
    getConsolidationShipmentsCsv,
  },
);

export default withConnect(ParcelInfoTab);
